import { useSignal } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import { invoke } from '$store/runtime.ts'
import { useCart } from 'apps/vtex/hooks/useCart.ts'
import { kitPdpDabelleUI } from '$store/sdk/kitPdpDabelleUI.ts'

interface ProductVariant {
  productID: string
  name: string
  image: { url: string; name?: string }[]
  offers: {
    offers: {
      price: number
      priceSpecification?: {
        priceType: string
        price: number
      }[]
    }[]
  }
  itemID?: string
  sku?: string
  skuId?: string
}

interface Product {
  productID: string
  name: string
  isVariantOf: {
    hasVariant: ProductVariant[]
  }
}

interface Props {
  kitValueArray: number[]
}

export default function BuyTogetherKit({ kitValueArray }: Props) {
  const productIdKit = kitValueArray
  const products = useSignal<Product[]>([])
  const isLoading = useSignal(true)
  const selectedVariants = useSignal<string[]>([])
  const dropdownStates = useSignal<boolean[]>([])
  const similarVariants = useSignal<Record<string, ProductVariant[]>>({})
  const { addItems } = useCart()
  const { tagKitDiscount } = kitPdpDabelleUI()

  useEffect(() => {
    const fetchKit = async () => {
      try {
        const res = await invoke['site'].loaders.kitCatalog({
          productIds: productIdKit,
        })

        if (res && Object.keys(res).length > 0) {
          const values = Object.values(res) as Product[]
          products.value = values

          const resSimilars = await invoke['site'].loaders.kitSimilars({
            productIds: productIdKit,
          })

          if (Array.isArray(resSimilars)) {
            const map: Record<string, ProductVariant[]> = {}
            resSimilars.forEach((item: any) => {
              if (item?.productID && item.variants?.length) {
                map[item.productID] = item.variants
              }
            })
            similarVariants.value = map
          }
        } else {
          console.warn('⚠️ Resposta vazia ou inválida:', res)
          products.value = []
        }
      } catch (err) {
        console.error('❌ Erro ao buscar kit:', err)
        products.value = []
      } finally {
        isLoading.value = false
      }
    }

    fetchKit()
  }, [])

  useEffect(() => {
    if (!isLoading.value) {
      selectedVariants.value = products.value.map(
        (product) => product.isVariantOf.hasVariant[0]?.productID || ''
      )
      dropdownStates.value = products.value.map(() => false)
    }
  }, [isLoading.value])

  const formatPriceLabel = (price?: number) => {
    if (!price || isNaN(price)) return 'R$ --'
    return `R$ ${price.toFixed(2).replace('.', ',')}`
  }

  const getListPrice = (priceSpec: any[] = []) => {
    const list = priceSpec.find(
      (p: any) => p.priceType === 'https://schema.org/ListPrice'
    )
    return list?.price ?? null
  }

  const calcOFF = (list?: number | null, sale?: number | null) => {
    if (!list || !sale || list <= sale) return null
    const pct = Math.round(((list - sale) / list) * 100)
    return pct > 0 ? pct : null
  }

  // 👉 Helper para achar a variante atualmente selecionada de um produto
  const getSelectedVariant = (product: Product, index: number) => {
    const original = product.isVariantOf.hasVariant
    const similar = similarVariants.value[product.productID] ?? []

    const variantsToShow = [...original, ...similar].filter(
      (v, i, self) => self.findIndex((x) => x.productID === v.productID) === i
    )

    const selected =
      variantsToShow.find(
        (v) => v.productID === selectedVariants.value[index]
      ) || variantsToShow[0]

    return { selected, variantsToShow }
  }

  // 👉 Recalcula subtotal e atualiza o sinal `tagKitDiscount` com a % final do desconto
  useEffect(() => {
    if (isLoading.value || products.value.length === 0) {
      tagKitDiscount.value = null as any
      return
    }

    let subtotalDe = 0
    let subtotalPor = 0

    products.value.forEach((product, index) => {
      const { selected } = getSelectedVariant(product, index)
      const offer = selected?.offers?.offers?.[0]
      const listPrice = getListPrice(offer?.priceSpecification)
      const salePrice = offer?.price

      // "De" usa o ListPrice quando existe, senão o próprio salePrice
      subtotalDe += listPrice ?? salePrice ?? 0
      // "Por" usa o salePrice
      subtotalPor += salePrice ?? 0
    })

    if (subtotalDe > 0 && subtotalPor > 0 && subtotalDe > subtotalPor) {
      const pct = Math.round(((subtotalDe - subtotalPor) / subtotalDe) * 100)
      // salva a % final no sinal (ex.: 23)
      ;(tagKitDiscount as any).value = pct
    } else {
      ;(tagKitDiscount as any).value = null
    }
  }, [
    isLoading.value,
    products.value,
    similarVariants.value,
    selectedVariants.value,
  ])

  const handleAddToCart = async () => {
    const items = products.value.map((product, index) => {
      const { selected } = getSelectedVariant(product, index)
      const itemId =
        selected.itemID || selected.skuId || selected.sku || selected.productID

      return {
        id: itemId,
        seller: '1',
        quantity: 1,
      }
    })

    await addItems({ orderItems: items })
  }

  if (isLoading.value) return <div>Carregando...</div>

  console.log(tagKitDiscount.value, 'tagKitDiscount')

  return (
    <div class="w-full mx-auto bg-[#F0F0F0] border border-[#F0F0F0] rounded font-sans shadow-sm p-5">
      <div class="text-[#DA1884] text-[22px] font-bold uppercase leading-normal pb-[14px]">
        KIT
      </div>

      {products.value.map((product, index) => {
        const original = product.isVariantOf.hasVariant
        const similar = similarVariants.value[product.productID] ?? []

        const variantsToShow = [...original, ...similar].filter(
          (v, i, self) =>
            self.findIndex((x) => x.productID === v.productID) === i
        )

        const variant =
          variantsToShow.find(
            (v) => v.productID === selectedVariants.value[index]
          ) || variantsToShow[0]

        const imageCard =
          variant?.image.find((img) => img.name !== 'FilterThumb') ||
          variant?.image[0]

        const offer = variant?.offers?.offers?.[0]
        const listPrice = getListPrice(offer?.priceSpecification)
        const salePrice = offer?.price
        const off = calcOFF(listPrice, salePrice)

        return (
          <div
            key={product.productID}
            class="bg-white px-4 py-4 flex gap-4 items-start mb-[14px] relative"
          >
            <a href={(variant as any)?.url} class="shrink-0">
              <img
                src={imageCard?.url || ''}
                alt={variant.name}
                class="w-[114px] h-[154px] object-cover rounded"
              />
            </a>

            <div class="flex-1">
              <p class="text-[18px] font-normal text-[#4A4A4A] leading-[140%] mb-[10px] h-[50px] line-clamp-2 overflow-hidden text-ellipsis">
                {variant.name}
              </p>

              {/* PREÇOS */}
              <div class="mb-[14px] flex flex-col">
                {listPrice && listPrice > (salePrice ?? 0) && (
                  <span class="text-[14px] text-[#595959] line-through mb-1">
                    {formatPriceLabel(listPrice)}
                  </span>
                )}

                <div class="flex items-center gap-3 flex-wrap">
                  <span class="text-[16px] font-bold text-[#2C2824]">
                    {formatPriceLabel(salePrice)}
                  </span>

                  {off && (
                    <span class="px-2 py-[6px] rounded-[6px] text-[#F9F5EE] text-[10px] font-bold bg-[#DA1884]">
                      -{off}% OFF
                    </span>
                  )}
                </div>
              </div>

              {/* Dropdown de variantes */}
              <div class="relative">
                <div
                  class="w-full flex items-center justify-between px-[15px] py-[10px] rounded-full border border-[#4A4A4A] bg-white cursor-pointer"
                  onClick={() => {
                    const copy = [...dropdownStates.value]
                    copy[index] = !copy[index]
                    dropdownStates.value = copy
                  }}
                >
                  <span class="text-sm text-[#333] line-clamp-1">
                    {variant.name || 'Cores'}
                  </span>
                  <svg width="11" height="12" viewBox="0 0 11 12" fill="none">
                    <path
                      d="M10.9923 3.19303C10.9923 3.37271 10.9281 3.53314 10.7998 3.67431L5.98705 8.48705C5.84588 8.62822 5.68545 8.69881 5.50578 8.69881C5.3261 8.69881 5.16568 8.62822 5.0245 8.48705L0.211761 3.67431C0.0705869 3.53314 0 3.37271 0 3.01336C0 2.85293 0.0705869 2.57059 0.211761 2.71176C0.352934 2.85293 0.513359 2.5 0.693035 2.5H10.3185C10.4982 2.5 10.6586 2.57059 10.7998 2.71176C10.941 2.85293 11.0051 3.01336 10.9923 3.19303Z"
                      fill="#4A4A4A"
                    />
                  </svg>
                </div>

                {dropdownStates.value[index] && (
                  <ul class="absolute z-20 mt-2 w-full bg-white border border-[#4A4A4A] rounded-[8px] text-sm shadow">
                    {variantsToShow.map((v) => {
                      const thumb =
                        v.image.find((img) => img.name === 'FilterThumb') ||
                        v.image[0]

                      return (
                        <li
                          key={v.productID}
                          class="px-4 py-2 hover:bg-[#F0F0F0] hover:rounded-[8px] cursor-pointer flex items-center gap-2"
                          onClick={() => {
                            const copy = [...selectedVariants.value]
                            copy[index] = v.productID
                            selectedVariants.value = copy

                            const dropCopy = [...dropdownStates.value]
                            dropCopy[index] = false
                            dropdownStates.value = dropCopy
                          }}
                        >
                          <img
                            src={thumb?.url}
                            alt={v.name}
                            class="w-5 h-5 rounded-full object-cover"
                          />
                          {v.name?.split(' - ').pop() || v.name}
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )
      })}

      {/* Subtotal */}
      <div class="bg-white px-4 pt-3 pb-2">
        <p class="text-[18px] font-normal text-[#4A4A4A] leading-[140%] mb-1">
          Subtotal:
        </p>
        {(() => {
          let subtotalDe = 0
          let subtotalPor = 0

          products.value.forEach((product, index) => {
            const { selected } = getSelectedVariant(product, index)
            const offer = selected?.offers?.offers?.[0]
            const listPrice = getListPrice(offer?.priceSpecification)
            const salePrice = offer?.price

            subtotalDe += listPrice ?? salePrice ?? 0
            subtotalPor += salePrice ?? 0
          })

          return (
            <p class="text-[16px] text-[#2C2824] leading-[140%]">
              {subtotalDe !== subtotalPor ? (
                <>
                  <span class="line-through text-[#999] mr-2">
                    De: {formatPriceLabel(subtotalDe)}
                  </span>
                  <strong class="font-bold text-[14px] lg:text-base">
                    Por: {formatPriceLabel(subtotalPor)}
                  </strong>
                </>
              ) : (
                <strong>{formatPriceLabel(subtotalPor)}</strong>
              )}
            </p>
          )
        })()}
      </div>

      <div class="bg-white px-4 pt-3 pb-4">
        <button
          class="w-full bg-[#0ea21f] hover:bg-[#0c8f1a] transition text-white text-[16px] font-bold leading-[16px] text-center h-[40px] rounded-[8px]"
          onClick={handleAddToCart}
        >
          Adicionar ao carrinho 
        </button>
      </div>
    </div>
  )
}
